import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import { formatCount, formatMoney, labelChannel } from "../campaignUi";

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return `${Math.round(n * 1000) / 10}%`;
}

function KpiCard({ icon, title, value, color, sub }) {
  return (
    <Card
      elevation={1}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        boxShadow: (th) =>
          th.palette.mode === "dark"
            ? `0 1px 4px ${alpha("#000", 0.35)}`
            : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", py: 2.25 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Avatar sx={{ bgcolor: color, width: 44, height: 44, color: "#fff" }}>{icon}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
              {value}
            </Typography>
            {sub ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35, lineHeight: 1.4 }}>
                {sub}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function CampaignMetricsHeader({ campaign, metrics }) {
  const theme = useTheme();
  const leads = metrics?.leads ?? 0;
  const conversions = metrics?.conversions ?? 0;
  const revenue = metrics?.revenue ?? 0;
  const messagesSent = metrics?.messagesSent ?? metrics?.dmsSent ?? 0;

  const conversionRate = useMemo(() => {
    if (!leads) return null;
    return conversions / leads;
  }, [conversions, leads]);

  const leadSource =
    metrics?.leadSource ?? metrics?.source ?? campaign?.channel ?? campaign?.channels?.[0] ?? null;

  const colors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ],
    [theme]
  );

  return (
    <Box sx={{ mb: 3.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.35, lineHeight: 1.3 }}>
        Resultados desta campanha
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75, lineHeight: 1.5 }}>
        Visão rápida do que a jornada já gerou: contatos, mensagens e conversões.
      </Typography>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KpiCard
            icon={<PeopleAltOutlinedIcon />}
            title="Contatos gerados"
            value={formatCount(leads) || "0"}
            color={colors[0]}
            sub="Pessoas que entraram pela campanha"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KpiCard
            icon={<SendOutlinedIcon />}
            title="Mensagens enviadas"
            value={formatCount(messagesSent) || "0"}
            color={colors[1]}
            sub={
              leadSource
                ? `Canal: ${labelChannel(leadSource) || leadSource}`
                : "Respostas e follow-ups da jornada"
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KpiCard
            icon={<EmojiEventsOutlinedIcon />}
            title="Conversões"
            value={formatCount(conversions) || "0"}
            color={colors[2]}
            sub={revenue ? `Receita ${formatMoney(revenue)}` : "Vendas ou metas concluídas"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <KpiCard
            icon={<PercentOutlinedIcon />}
            title="Taxa de conversão"
            value={formatPct(conversionRate)}
            color={colors[3]}
            sub="Conversões ÷ contatos gerados"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
